import { useState } from "react";
import { Navigate } from "react-router-dom";
import { LogIn, Lock, Mail, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { SEO } from "../components/medical/SEO";

const formatError = (detail) => {
    if (!detail) return "Login failed. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
    return String(detail);
};

export const AdminLogin = () => {
    const { user, ready, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (ready && user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-50/60 via-white to-orange-50/40 flex items-center justify-center px-5">
            <SEO title="Admin Login" description="TruHeal India admin dashboard login." />
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <img
                        src="https://res.cloudinary.com/deq3rnrha/image/upload/v1777362730/57F66F69-6741-41CE-9E1B-06B8AB12B13D_izmfua.png"
                        alt="TruHeal India"
                        className="h-24 w-auto mx-auto"
                    />
                    <h1 className="mt-4 font-display text-3xl font-bold text-stone-900">
                        Admin Dashboard
                    </h1>
                    <p className="mt-2 text-sm text-stone-500">
                        Sign in to manage patient enquiries.
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="admin-login-form"
                    className="rounded-3xl bg-white border border-stone-200 shadow-xl p-8 space-y-5"
                >
                    <div className="space-y-2">
                        <Label htmlFor="al-email">Email</Label>
                        <div className="relative">
                            <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <Input
                                id="al-email"
                                data-testid="admin-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@truhealindia.com"
                                className="h-12 rounded-xl pl-9"
                                required
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="al-password">Password</Label>
                        <div className="relative">
                            <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <Input
                                id="al-password"
                                data-testid="admin-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="h-12 rounded-xl pl-9"
                                required
                            />
                        </div>
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full rounded-full bg-brand-700 hover:bg-brand-800 text-white h-12 text-base shadow-lg"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Signing in...
                            </>
                        ) : (
                            <>
                                <LogIn className="h-4 w-4 mr-2" />
                                Sign in
                            </>
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
};
